import { Link } from "react-router-dom";
import { getAppVersion, getVersionDisplay } from "@/lib/version";

/**
 * Shared footer component for public-facing pages
 * Features:
 * - Logo links to home
 * - All links visible on mobile (horizontal row)
 * - 4-column grid on desktop
 */
const Footer = () => {
    return (
        <footer className="bg-primary text-primary-foreground">
            <div className="max-w-7xl mx-auto py-6 md:py-8 px-4 sm:px-6 lg:px-8">
                {/* Desktop: 4-column grid, Mobile: logo + horizontal links */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
                    {/* Logo section - always visible, links to home */}
                    <div className="space-y-2">
                        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                            <img src="/assets/logo_new_outline.png" alt="Logo" className="h-12 w-12 md:h-16 md:w-16 object-contain" />
                            <h2 className="text-base md:text-lg font-bold flex flex-col items-start leading-tight text-primary-foreground" aria-label="The Love Contract">
                                <span>The</span>
                                <span>Love</span>
                                <span>Contract™</span>
                            </h2>
                        </Link>
                        <p className="text-xs md:text-sm text-primary-foreground/80 hidden md:block">
                            Crafting your love story, together.
                        </p>
                    </div>

                    {/* Product - visible on desktop as column, hidden on mobile (shown in combined row below) */}
                    <div className="hidden md:block">
                        <h3 className="text-sm font-semibold tracking-wider uppercase mb-3">Product</h3>
                        <ul className="space-y-1.5">
                            <li><Link className="text-sm text-primary-foreground/80 hover:text-primary-foreground" to="/journey">The Journey</Link></li>
                            <li><Link className="text-sm text-primary-foreground/80 hover:text-primary-foreground" to="/story">Our Story</Link></li>
                        </ul>
                    </div>

                    {/* Company - visible on desktop as column, hidden on mobile (shown in combined row below) */}
                    <div className="hidden md:block">
                        <h3 className="text-sm font-semibold tracking-wider uppercase mb-3">Company</h3>
                        <ul className="space-y-1.5">
                            <li><Link className="text-sm text-primary-foreground/80 hover:text-primary-foreground" to="/contact">Contact Us</Link></li>
                        </ul>
                    </div>

                    {/* Legal - visible on desktop as column, hidden on mobile (shown in combined row below) */}
                    <div className="hidden md:block">
                        <h3 className="text-sm font-semibold tracking-wider uppercase mb-3">Legal</h3>
                        <ul className="space-y-1.5">
                            <li><Link to="/privacy" className="text-sm text-primary-foreground/80 hover:text-primary-foreground">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="text-sm text-primary-foreground/80 hover:text-primary-foreground">Terms of Service</Link></li>
                            <li><Link to="/cookie-policy" className="text-sm text-primary-foreground/80 hover:text-primary-foreground">Cookie Policy</Link></li>
                            <li><Link to="/refund-policy" className="text-sm text-primary-foreground/80 hover:text-primary-foreground">Refund Policy</Link></li>
                        </ul>
                    </div>

                    {/* Mobile: All links in a single horizontal row */}
                    <div className="md:hidden col-span-1">
                        <ul className="flex flex-wrap gap-x-4 gap-y-2">
                            <li><Link to="/journey" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">The Journey</Link></li>
                            <li><Link to="/story" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Our Story</Link></li>
                            <li><Link to="/contact" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Contact Us</Link></li>
                            <li><Link to="/privacy" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Privacy Policy</Link></li>
                            <li><Link to="/terms" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Terms of Service</Link></li>
                            <li><Link to="/cookie-policy" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Cookie Policy</Link></li>
                            <li><Link to="/refund-policy" className="text-xs text-primary-foreground/80 hover:text-primary-foreground">Refund Policy</Link></li>
                        </ul>
                    </div>
                </div>

                <div className="mt-4 md:mt-6 border-t border-primary-foreground/20 pt-3 md:pt-4 flex flex-col md:flex-row justify-between items-center gap-2">
                    <p className="text-xs text-primary-foreground/60">
                        © {new Date().getFullYear()} The Love Contract™. All rights reserved.
                    </p>
                    <p
                        className="text-xs text-primary-foreground/60 cursor-default"
                        title={`Build: ${getAppVersion().commit} | ${new Date(getAppVersion().buildDate).toLocaleDateString()}`}
                    >
                        {getVersionDisplay()}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
